
import { Component } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'
import BaseLayout from '@/shared/components/BaseLayout.vue'
import Campaign from '@/api/models/Campaign'
import Reference from '@/api/models/Reference'
import { RootStateMutations } from '@/store/mutations'
import { getAccountId } from '@/service/storage'

@Component({
    components: {
        BaseLayout
    }
})

export default class Campaigns extends BaseLayout {
    @Getter('currentAccount')
    account!: number

    @Getter('campaigns')
    campaigns!: Array<Campaign>

    @Getter('sites')
    sites!: Array<Reference>

    @Action('loadCampaigns')
    loadCampaigns!: () => Promise<void>

    @Action('loadSites')
    loadSites!: () => Promise<void>

    @Action('selectCampaign')
    selectCampaign!: (campaign: Campaign) => void

    @Action('createCampaign')
    createCampaign!: (campaign: Campaign) => Promise<void>

    @Action('updateCampaign')
    updateCampaign!: (campaign: Campaign) => Promise<void>

    @Action('deleteCampaign')
    deleteCampaign!: (campaignId: number|undefined) => void

    @Action('deleteCampaignItems')
    deleteCampaignItems!: (campaignId: number|undefined) => void

    @Action('getAct')
    getAct!: (campaignId: number|undefined) => Promise<void>

    private showPopup = false
    private popupAction = ''
    private id: number|undefined = 0
    private campaignId = ''
    private token = ''
    private btnDisabled = false

    private selectedSite: Reference = {
        code: '',
        name: ''
    }

    async created (): Promise<void> {
        if (!this.account) {
            const accountId = getAccountId() || 0
            if (!accountId) {
                await this.$router.push({ name: 'accounts' })
            } else {
                this.$store.commit(RootStateMutations.setAccount, accountId)
            }
        }

        this.$store.commit(RootStateMutations.setLoading, true)

        await this.handleCallback(async () => {
            await this.loadCampaigns()
        })
        await this.handleCallback(async () => {
            await this.loadSites()
        })
        this.$store.commit(RootStateMutations.setLoading, false)
    }

    async chooseCampaign (campaign: Campaign): Promise<void> {
        this.selectCampaign(campaign)
        await this.$router.push({ name: 'base_settings' })
    }

    async showCreatePopup (): Promise<void> {
        this.popupAction = 'create'
        this.resetForm()
        this.showPopup = true
    }

    async showUpdatePopup (campaign: Campaign): Promise<void> {
        this.popupAction = 'update'
        this.id = campaign.id
        this.campaignId = campaign.campaignId
        this.setCampaignSite(campaign)
        this.showPopup = true
    }

    private setCampaignSite (campaign: Campaign): void {
        const currentSite = this.sites.find(value => {
            return campaign.site === value.code
        })
        if (!currentSite) {
            this.selectedSite = {
                code: '',
                name: ''
            }
        } else {
            this.selectedSite = currentSite
        }
    }

    private getSiteName (code: string): string {
        const find = this.sites.find(value => {
            return value.code === code
        })

        return find?.name ?? ''
    }

    async addCampaign (): Promise<void> {
        const newCampaign: Campaign = {
            id: 0,
            campaignId: this.campaignId,
            site: this.selectedSite.code
        }

        this.btnDisabled = true

        await this.handlePopup(this.createCampaign, newCampaign, this.$t('campaigns.createSuccess'))
    }

    async editCampaign (id: number): Promise<void> {
        const updateCampaign: Campaign = {
            id: id,
            campaignId: this.campaignId,
            site: this.selectedSite.code
        }

        this.btnDisabled = true

        await this.handlePopup(this.updateCampaign, updateCampaign, this.$t('campaigns.updateSuccess'))
    }

    async handlePopup (method: (campaign: Campaign) => Promise<void>, campaign: Campaign, message: string) {
        await (this.$refs.baseLayout as BaseLayout).handleRequest({
            tryBody: async () => await method(campaign),
            onSuccess: () => {
                this.showPopup = false
                this.$refs.baseLayout.showSuccess(message)
            },
            onFailure: (errorMsg: string) => {
                this.showPopup = false
                this.$refs.baseLayout.showError(errorMsg)
            },
            onFinal: () => this.resetForm()
        })
    }

    async removeCampaign (campaign: Campaign): Promise<void> {
        await (this.$refs.baseLayout as BaseLayout).handleWithAlerts(async () => {
            await this.deleteCampaign(campaign.id)
        }, this.$t('campaigns.successDelete'))
    }

    async removeCampaignItems (campaign: Campaign): Promise<void> {
        await (this.$refs.baseLayout as BaseLayout).handleWithAlerts(async () => {
            await this.deleteCampaignItems(campaign.id)
        }, this.$t('campaigns.successDeleteItems'))
    }

    async downloadAct (campaign: Campaign): Promise<void> {
        await (this.$refs.baseLayout as BaseLayout).handleRequest({
            tryBody: async () => {
                await this.getAct(campaign.id)
            },
            onFailure: () => this.$refs.baseLayout.showError(this.$t('campaigns.actError'))
        })
    }

    private resetForm (): void {
        this.id = 0
        this.selectedSite = { code: '', name: '' }
        this.campaignId = ''
        this.btnDisabled = false
    }
}
